/**
 * Divisa de visualización del panel.
 *
 * Única fuente del símbolo monetario que se pinta en toda la app. Los importes
 * NO se convierten: solo cambia el símbolo/código con el que se muestran.
 *
 *  - La divisa por defecto viene del Customer (campo Currency, ISO 4217) y se
 *    resuelve en el login vía GET /customers/me/currency (los sub-usuarios
 *    heredan la del cliente padre; los ADMIN arrancan en EUR).
 *  - El usuario puede cambiarla desde la barra superior (CurrencySelector); esa
 *    elección se guarda por usuario en localStorage y se descarta en el próximo
 *    login "fresco" para volver a la divisa del cliente.
 *
 * El estado vivo está en redux (reducers/currency.js); este módulo mantiene una
 * copia síncrona (`setActiveCurrency`, sincronizada desde store/index.js) para
 * que cualquier helper de formato — incluso fuera de componentes React — pueda
 * leer el símbolo actual sin necesidad de suscribirse. AppShell remonta el
 * contenido (key={currencyCode}) cuando cambia, así que los componentes se
 * re-renderizan con el símbolo nuevo.
 *
 * La tabla SYMBOLS es la misma que usa el kiosco (lockers-app qml/currencies.js):
 * si se añade una divisa aquí, revisar también allí.
 */

export const DEFAULT_CURRENCY = 'EUR'

export const SYMBOLS = {
  // Europa
  EUR: '€',
  GBP: '£',
  CHF: 'fr',
  CZK: 'Kč',
  NOK: 'kr',
  SEK: 'kr',
  DKK: 'kr',
  ISK: 'kr',
  PLN: 'zł',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  RSD: 'дин',
  MKD: 'ден',
  BAM: 'KM',
  ALL: 'lek',
  MDL: 'lei',
  UAH: '₴',
  BYN: 'Br',
  RUB: '₽',
  TRY: '₺',
  GEL: '₾',
  AMD: 'AMD',
  AZN: '₼',
  KZT: '₸',
  UZS: 'UZS',
  GIP: '£',
  HRK: 'kn',

  // América
  USD: '$',
  CAD: 'C$',
  MXN: '$',
  BRL: 'R$',
  ARS: '$',
  CLP: '$',
  COP: '$',
  PEN: 'S/',
  UYU: '$U',
  DOP: 'RD$',
  CRC: '₡',
  GTQ: 'Q',
  PAB: 'B/.',
  BOB: 'Bs',
  PYG: '₲',
  VES: 'Bs',

  // Asia - Pacífico
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  TWD: 'NT$',
  HKD: 'HK$',
  SGD: 'S$',
  MYR: 'RM',
  THB: '฿',
  VND: '₫',
  PHP: '₱',
  IDR: 'Rp',
  INR: '₹',
  PKR: 'Rs',
  BDT: '৳',
  LKR: 'Rs',
  NPR: 'Rs',
  MVR: 'Rf',
  MMK: 'K',
  AUD: 'A$',
  NZD: 'NZ$',
  FJD: 'FJ$',
  XPF: 'F',

  // Oriente Medio
  AED: 'AED',
  SAR: 'SAR',
  QAR: 'QAR',
  KWD: 'KWD',
  BHD: 'BHD',
  OMR: 'OMR',
  JOD: 'JOD',
  ILS: '₪',

  // África
  EGP: 'E£',
  MAD: 'MAD',
  TND: 'TND',
  DZD: 'DZD',
  LYD: 'LYD',
  ZAR: 'R',
  NGN: '₦',
  KES: 'KSh',
  GHS: '₵',
  TZS: 'TSh',
  UGX: 'USh',
  XOF: 'CFA',
  XAF: 'CFA',
  MUR: 'Rs',
  ETB: 'Br',
}

// Divisas destacadas: se listan primero en el selector.
export const FEATURED = ['EUR', 'USD', 'GBP', 'CHF', 'CZK', 'PLN', 'SEK', 'NOK', 'DKK', 'TRY', 'MAD', 'AED', 'MXN', 'BRL']

// Nombres ISO 4217 (mismos que el paquete currency-codes; embebidos para no añadir dependencia).
const NAMES = {
  EUR: 'Euro',
  GBP: 'Pound Sterling',
  CHF: 'Swiss Franc',
  CZK: 'Czech Koruna',
  NOK: 'Norwegian Krone',
  SEK: 'Swedish Krona',
  DKK: 'Danish Krone',
  ISK: 'Iceland Krona',
  PLN: 'Zloty',
  HUF: 'Forint',
  RON: 'Romanian Leu',
  BGN: 'Bulgarian Lev',
  RSD: 'Serbian Dinar',
  MKD: 'Denar',
  BAM: 'Convertible Mark',
  ALL: 'Lek',
  MDL: 'Moldovan Leu',
  UAH: 'Hryvnia',
  BYN: 'Belarusian Ruble',
  RUB: 'Russian Ruble',
  TRY: 'Turkish Lira',
  GEL: 'Lari',
  AMD: 'Armenian Dram',
  AZN: 'Azerbaijan Manat',
  KZT: 'Tenge',
  UZS: 'Uzbekistan Sum',
  GIP: 'Gibraltar Pound',
  HRK: 'Kuna',
  USD: 'US Dollar',
  CAD: 'Canadian Dollar',
  MXN: 'Mexican Peso',
  BRL: 'Brazilian Real',
  ARS: 'Argentine Peso',
  CLP: 'Chilean Peso',
  COP: 'Colombian Peso',
  PEN: 'Sol',
  UYU: 'Peso Uruguayo',
  DOP: 'Dominican Peso',
  CRC: 'Costa Rican Colon',
  GTQ: 'Quetzal',
  PAB: 'Balboa',
  BOB: 'Boliviano',
  PYG: 'Guarani',
  VES: 'VES',
  JPY: 'Yen',
  CNY: 'Yuan Renminbi',
  KRW: 'Won',
  TWD: 'New Taiwan Dollar',
  HKD: 'Hong Kong Dollar',
  SGD: 'Singapore Dollar',
  MYR: 'Malaysian Ringgit',
  THB: 'Baht',
  VND: 'Dong',
  PHP: 'Philippine Piso',
  IDR: 'Rupiah',
  INR: 'Indian Rupee',
  PKR: 'Pakistan Rupee',
  BDT: 'Taka',
  LKR: 'Sri Lanka Rupee',
  NPR: 'Nepalese Rupee',
  MVR: 'Rufiyaa',
  MMK: 'Kyat',
  AUD: 'Australian Dollar',
  NZD: 'New Zealand Dollar',
  FJD: 'Fiji Dollar',
  XPF: 'CFP Franc',
  AED: 'UAE Dirham',
  SAR: 'Saudi Riyal',
  QAR: 'Qatari Rial',
  KWD: 'Kuwaiti Dinar',
  BHD: 'Bahraini Dinar',
  OMR: 'Rial Omani',
  JOD: 'Jordanian Dinar',
  ILS: 'New Israeli Sheqel',
  EGP: 'Egyptian Pound',
  MAD: 'Moroccan Dirham',
  TND: 'Tunisian Dinar',
  DZD: 'Algerian Dinar',
  LYD: 'Libyan Dinar',
  ZAR: 'Rand',
  NGN: 'Naira',
  KES: 'Kenyan Shilling',
  GHS: 'Ghana Cedi',
  TZS: 'Tanzanian Shilling',
  UGX: 'Uganda Shilling',
  XOF: 'CFA Franc BCEAO',
  XAF: 'CFA Franc BEAC',
  MUR: 'Mauritius Rupee',
  ETB: 'Ethiopian Birr',
}

const nameOf = (code) => NAMES[code] || code

/** Lista completa soportada: [{ code, symbol, name }], destacadas primero. */
export const CURRENCIES = (() => {
  const all = Object.keys(SYMBOLS)
  const rest = all.filter((c) => !FEATURED.includes(c)).sort()
  return [...FEATURED.filter((c) => SYMBOLS[c]), ...rest].map((code) => ({
    code,
    symbol: SYMBOLS[code],
    name: nameOf(code),
  }))
})()

/** Código válido (en tabla) en mayúsculas, o '' si no lo es. */
export function normalizeCurrency(code) {
  const c = code === undefined || code === null ? '' : String(code).trim().toUpperCase()
  return SYMBOLS[c] !== undefined ? c : ''
}

/** Símbolo de una divisa; si no está en la tabla devuelve el propio código, nunca vacío. */
export function symbolFor(code) {
  const c = code === undefined || code === null ? '' : String(code).trim().toUpperCase()
  if (c === '') return SYMBOLS[DEFAULT_CURRENCY]
  return SYMBOLS[c] !== undefined ? SYMBOLS[c] : c
}

// ── Estado activo (copia síncrona del slice redux `currency`) ──────────────

let activeCode = DEFAULT_CURRENCY

export function setActiveCurrency(code) {
  activeCode = normalizeCurrency(code) || DEFAULT_CURRENCY
}

export function currencyCode() {
  return activeCode
}

export function currencySymbol() {
  return symbolFor(activeCode)
}

/**
 * Formatea un importe con la divisa activa: "1.234,56 €" (número en es-ES +
 * símbolo como sufijo, igual que el kiosco). Sustituye a
 * `x.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })`, cuya
 * salida para EUR es idéntica.
 */
export function formatMoney(value, opts = {}) {
  const n = Number(value)
  // Texto no numérico ("N/A", importes ya formateados...) se devuelve tal cual,
  // igual que hacía String.prototype.toLocaleString en el código original.
  if (!Number.isFinite(n)) return typeof value === 'string' && value.trim() !== '' ? value : '—'
  const { minimumFractionDigits = 2, maximumFractionDigits = 2, locale = 'es-ES' } = opts
  return `${n.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits })} ${currencySymbol()}`
}

// ── Persistencia por usuario (localStorage) ────────────────────────────────

const overrideKey = (authId) => `currency:override:${authId || 'anon'}`
const defaultKey = (authId) => `currency:default:${authId || 'anon'}`

const safeGet = (k) => {
  try { return localStorage.getItem(k) } catch (e) { return null }
}
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v) } catch (e) { /* storage bloqueado: ignorar */ }
}
const safeRemove = (k) => {
  try { localStorage.removeItem(k) } catch (e) { /* ignorar */ }
}

export function readStoredOverride(authId) {
  return normalizeCurrency(safeGet(overrideKey(authId)))
}
export function writeStoredOverride(authId, code) {
  const c = normalizeCurrency(code)
  if (c) safeSet(overrideKey(authId), c)
}
export function clearStoredOverride(authId) {
  safeRemove(overrideKey(authId))
}
export function readStoredDefault(authId) {
  return normalizeCurrency(safeGet(defaultKey(authId)))
}
export function writeStoredDefault(authId, code) {
  const c = normalizeCurrency(code)
  if (c) safeSet(defaultKey(authId), c)
}
